import { AlertBar, Button, Typography } from '@packages/ui/shared';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

/** Style for the alert bar inside the delete case modal. */
const alertBarStyle = { marginBottom: 24 };

/** Style for the cancel button's right margin. */
const cancelButtonStyle = { marginRight: 2 };

/**
 * Props for the DeleteCaseModal component.
 *
 * Controls visibility, loading state, and user actions for the
 * delete case confirmation modal.
 */
export type DeleteCaseModalProps = {
    /** The alert bar message to display inside the modal (empty string hides it). */
    alertBarMessage: string;

    /** Whether the case is currently being deleted (disables buttons, shows spinner). */
    isDeleting: boolean;

    /** Whether this modal is currently visible. */
    isVisible: boolean;

    /** Called when the user dismisses the modal alert bar. */
    onAlertBarClose: () => void;

    /** Called when the user clicks Cancel to close the modal without action. */
    onCancel: () => void;

    /** Called when the user confirms deletion. */
    onDelete: () => void;
};

/**
 * Delete case confirmation modal for the CID-CVN worksheet subview.
 * Asks the user to confirm before permanently deleting the draft case.
 *
 * @param props - component props
 * @returns the delete case confirmation modal
 */
export function DeleteCaseModal({
    alertBarMessage,
    isDeleting,
    isVisible,
    onAlertBarClose,
    onCancel,
    onDelete,
}: DeleteCaseModalProps) {
    // Hooks
    const { t } = useTranslation();

    return (
        <Modal centered onHide={onCancel} show={isVisible}>
            <Modal.Header>
                <Modal.Title>{t('techline:deleteCaseModal.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <AlertBar error={true} onClose={onAlertBarClose} show={Boolean(alertBarMessage)} style={alertBarStyle}>
                    <Typography>{alertBarMessage}</Typography>
                </AlertBar>
                <Typography>{t('techline:deleteCaseModal.message')}</Typography>
            </Modal.Body>
            <Modal.Footer>
                <Button disabled={isDeleting} onPress={onCancel} style={cancelButtonStyle} variant="ghost-blue">
                    {t('buttons:cancel')}
                </Button>
                <Button disabled={isDeleting} isLoading={isDeleting} onPress={onDelete} variant="danger">
                    {t('buttons:delete')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
